import type {
  Opportunity,
  OpportunityStatus,
  OpportunitySummary,
  ProductSlice,
} from '@/types/Opportunity';

/** Won / Lost / Open, derived from the raw Salesforce flags.
 *  isClosed === false is always Open, even if isWon is set — that combination
 *  is contradictory source data, and treating it as Open keeps the summary
 *  chips consistent with the pie chart, which filters on isClosed alone. */
export function opportunityStatus(o: Opportunity): OpportunityStatus {
  if (!o.isClosed) return 'Open';
  return o.isWon ? 'Won' : 'Lost';
}

export function isOpen(o: Opportunity): boolean {
  return !o.isClosed;
}

/** Weighted revenue per product across OPEN opportunities only, largest
 *  first. Zero-valued groups are dropped so they do not render as invisible
 *  slices with visible legend entries. Every non-zero group is returned —
 *  deliberately not passed through formatDoughnutChartData, whose 5-slice cap
 *  rolls the remainder into a synthetic "Other" that would collide with the
 *  real "Other" product category. */
export function weightedRevenueByProduct(rows: Opportunity[]): ProductSlice[] {
  const totals = new Map<string, number>();

  for (const o of rows.filter(isOpen)) {
    totals.set(o.product, (totals.get(o.product) ?? 0) + (o.weightedRevenue ?? 0));
  }

  return [...totals.entries()]
    .map(([label, value]) => ({ label, value }))
    .filter((s) => s.value !== 0)
    .sort((a, b) => b.value - a.value);
}

export function summarize(rows: Opportunity[]): OpportunitySummary {
  const open = rows.filter(isOpen);

  return {
    openCount: open.length,
    wonCount: rows.filter((o) => opportunityStatus(o) === 'Won').length,
    lostCount: rows.filter((o) => opportunityStatus(o) === 'Lost').length,
    openWeightedRevenue: open.reduce((sum, o) => sum + (o.weightedRevenue ?? 0), 0),
  };
}
